/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: ShapeFactory.ts
 * Project: mle-client-n
 * Committed last: 2022/11/25 @ 116
 * ---
 * Description:
 */

import Pamela from "@revodigital/pamela";
import {OBJECT_NAME} from "../../../../../../common/pamela-names";
import {RichTextSource} from "@revodigital/pamela/lib/shapes/RichText";
import {defaultConfig, shapeConfig} from "../../../../../../config";
import {TableBuilder} from "@revodigital/pamela/lib/builders/TableBuilder";
import {ColumnBuilder} from "@revodigital/pamela/lib/builders/ColumnBuilder";
import {HorizontalAlignment} from "@revodigital/pamela/lib/configuration/Alignment";

/**
 * Elenca le opzioni predefinite per le forme aggiunte al piano di lavoro.
 * Evita l'aggiunta di forme da differenti componenti dell'work-area con proprietà inconsistenti tra loro.
 */
export const DefaultShapesOptions = {
    BORDER: {
        borderColor: '#48AFF0',
        cornerColor: '#2B95D6',
        cornerSize: 10,
        transparentCorners: false,
        cornerStyle: 'circle',
        strokeWidth: 0
    } as Pamela.ShapeConfig,
    LOCK_SCALING: {
        lockSkewingY: true,
        lockSkewingX: true,
        lockScalingY: true,
        lockScalingX: true,
        lockScalingFlip: true,
    },
    SIZE_FILL: {
        width: 100,
        height: 100,
        fill: Pamela.Util.getRandomColor(),
        name: OBJECT_NAME,
        draggable: true
    },
    BASE_TEXT: {
        width: 200,
        height: 80,
        draggable: true,
        fontSize: 20,
        fill: 'black',
        text: 'Testo semplice',
        editable: true,
        backgroundColor: 'transparent',
    } as Pamela.TextConfig,
    RICH_TEXT: {
        width: 200,
        height: 200,
        sourceType: RichTextSource.Html,
        htmlContent: 'Lorem <b>ipsum</b> dolor sit amet',
        backgroundColor: 'transparent',
        fill: 'black',
        fontSize: 15,
        draggable: true,
    } as Pamela.RichTextConfig,
    TABLE: {
        fill: 'white',
        width: 400,
        height: 200,
        headerFill: '#8A9BA8',
        internalBorder: {
            borderColor: 'black',
            borderWidth: 2,
            bordered: true
        },
        headerText: {
            fontSize: 16
        },
        externalBorder: {
            bordered: true,
            borderWidth: 2,
            borderColor: 'black'
        },
        header: [
            {width: 'auto', text: 'Proprietà',},
            {
                width: 'auto',
                text: 'Valori Nutrizionali',
            },
        ],
        draggable: true,
        rows: [
            {data: ['Energia', '1489kj'], height: 'auto', fill: 'white'},
            {data: ['Grassi', '1,0g'], height: 'auto', fill: 'white'},
            {data: ['Carboidrati', '70g'], height: 'auto', fill: 'white'},
            {data: ['Fibre', '3,0g'], height: 'auto', fill: 'white'},
        ],
        headerHeight: 'auto',
    } as Pamela.TableConfig,
    EXPORT_VARIABLE: {
        variableName: 'dataScadenza',
        fill: 'yellow',
        draggable: true,
    } as Pamela.ExportVariableConfig,
    BARCODE: {
        width: 200,
        height: 200,
        perfectDrawEnabled: false,
        code: '4343239fdk',
        draggable: true,
        displayValue: true,
        encoding: 'CODE39',
        contentFontSize: 20,
        fill: 'black',
        x: 20,
        y: 20,
    } as Pamela.BarcodeConfig,
    IMAGE: {
        draggable: true,
    }
};

/**
 * Opzioni da usare per la creazione di una forma
 */
export interface ShapeCreationOptions<T extends Pamela.ShapeConfig = Pamela.ShapeConfig> {
    defaultOptions?: Partial<T>;
    position?: Pamela.Vector2d;
    options?: Partial<T>;
    color?: string;
}

export class ShapeFactory {
    /**
     * Restituisce la coordinata x di una forma, sulla base delle sue opzioni
     * @param options
     * @private
     */
    private getX(options: ShapeCreationOptions): number {
        if (options.position) return options.position.x;
        else return 10;
    }

    /**
     * Restituisce la coordinata y di una forma, sulla base delle sue opzioni
     * @param options
     * @private
     */
    private getY(options: ShapeCreationOptions): number {
        if (options.position) return options.position.y;
        else return 10;
    }

    /**
     * Produce una forma del tipo 'Lista ingredienti'
     * NOTA: Il collegamento al dataref deve essere fatto successivamente
     */
    public produceIngredientsList(): Pamela.RichText {
        return this.produceRichText(`{{${defaultConfig.dataOrigins.ingredientsList}}}`);
    }

    /**
     * Restituisce le opzioni di default
     * @param options
     * @private
     */
    private getDefaultOptions(options: ShapeCreationOptions): Partial<Pamela.ShapeConfig> {
        if (options.defaultOptions) return options.defaultOptions;
        else return DefaultShapesOptions.SIZE_FILL;
    }

    /**
     * Crea un rettangolo specificando delle opzioni predefinite
     * @param options Opzioni per la creazione
     */
    public produceShapeConfig<T extends Pamela.ShapeConfig = Pamela.ShapeConfig>(options: ShapeCreationOptions<T>): T {
        return {
            ...this.getDefaultOptions(options),
            x: this.getX(options),
            y: this.getY(options),
            fill: this.getColor(options),
            ...options
        } as T
    }

    /**
     * Crea un rettangolo con le opzioni predefinite
     * @param additionalOptions
     */
    public produceRectangle(additionalOptions?: Partial<Pamela.ShapeConfig>): Pamela.Rect {
        return new Pamela.Rect(this.produceShapeConfig({defaultOptions: DefaultShapesOptions.SIZE_FILL, ...additionalOptions}));
    }

    /**
     * Crea un cerchio
     * @param additionalOptions Opzioni aggiuntive per la creazione della forma
     */
    public produceCircle(additionalOptions?: Partial<Pamela.ShapeConfig>): Pamela.Circle {
        return new Pamela.Circle(this.produceShapeConfig({
            defaultOptions: DefaultShapesOptions.SIZE_FILL,
            options: additionalOptions
        }))
    }

    public produceMyLabelTable(): Pamela.Table {
        return this.getMyLabelTableBuilder().build();
    }

    /**
     * Crea una tabella base
     */
    public produceTable(): Pamela.Table {
        const defaultBuilder = this.getDefaultTableBuilder();
        return defaultBuilder.build()
    }

    private getMyLabelTableBuilder(): TableBuilder {
        const tBuilder = new TableBuilder({
            width: 300,
            x: 100,
            y: 100,
            height: 300,
            draggable: true,
            stroke: 'black',
            strokeWidth: 2
        });

        const borderPreset = {
            bordered: true,
            borderWidth: 2,
            borderColor: 'black'
        } as Pamela.BorderConfig;

        tBuilder
            .addColumn({
                column: ColumnBuilder
                    .withCells(10, {
                        bold: true,
                        bottomBorder: borderPreset,
                        rightBorder: borderPreset,
                        topBorder: borderPreset,
                        textAlign: HorizontalAlignment.Left,
                        textColor: 'black',
                        padding: 5,
                        fontSize: 20
                    })
                    .customWidth(70)
                    .setFirst({
                        bold: false
                    })
                    .populate([
                        'Valori nutrizionali',
                        'Energia',
                        '',
                        'Grassi',
                        'di cui grassi saturi',
                        'Carboidrati',
                        'di cui zuccheri',
                        'Fibre',
                        'Proteine',
                        'Sale'
                    ])
                    .set(3, {
                        textAlign: HorizontalAlignment.Left,
                        bold: true
                    })
                    .set(4, {textAlign: HorizontalAlignment.Right, bold: false})
                    .set(5, {
                        textAlign: HorizontalAlignment.Left,
                        bold: true
                    })
                    .set(6, {textAlign: HorizontalAlignment.Right, bold: false})
            })
            .addColumn({
                column: ColumnBuilder
                    .withCells(10, {
                        bottomBorder: borderPreset,
                        rightBorder: borderPreset,
                        topBorder: borderPreset,
                        textAlign: HorizontalAlignment.Right,
                        textColor: 'black',
                        padding: 8,
                        fontSize: 20
                    })
                    .setFirst({bold: true})
                    .autoWidth()
                    .populate(shapeConfig.myLabelTable.dataOrigins.map((it: any) => `${it.key} ${it.code}`))
            });

        return tBuilder;
    };

    /**
     * Crea un triangolo
     * @param additionalOptions Opzioni aggiuntive
     */
    public produceTriangle(additionalOptions?: Partial<Pamela.ShapeConfig>): Pamela.Shape {
        return new Pamela.RegularPolygon(this.produceShapeConfig<Pamela.RegularPolygonConfig>({
            defaultOptions: DefaultShapesOptions.SIZE_FILL,
            position: {
                x: 50,
                y: 50,
            },
            options: {
                sides: 3,
                radius: 50,
                ...additionalOptions
            },
        }))
    }

    /**
     * Produce un testo semplice
     * @param additionalOptions
     * @param text Testo iniziale
     */
    public produceSimpleText(text?: string, additionalOptions?: Partial<Pamela.TextConfig>): Pamela.Text {
        let cont = DefaultShapesOptions.BASE_TEXT.text;
        if (text) cont = text;

        if (additionalOptions)
            delete additionalOptions['text'];

        const options = {...DefaultShapesOptions.BASE_TEXT};
        options.text = cont;

        return new Pamela.Text(this.produceShapeConfig<Pamela.TextConfig>({
            defaultOptions: options,
            options: {
                ...additionalOptions
            }
        }))
    }

    /**
     * Crea un'ellisse
     * @param additionalOptions
     */
    public produceEllipse(additionalOptions?: Partial<Pamela.ShapeConfig>): Pamela.Ellipse {
        return new Pamela.Ellipse(this.produceShapeConfig<Pamela.EllipseConfig>({
            defaultOptions: DefaultShapesOptions.SIZE_FILL,
            options: {
                radiusX: 100,
                radiusY: 50,
                ...additionalOptions
            },
        }))
    }

    /**
     * Crea un cuneo
     * @param additionalOptions
     */
    public produceWedge(additionalOptions?: Partial<Pamela.ShapeConfig>): Pamela.Wedge {
        return new Pamela.Wedge(this.produceShapeConfig<Pamela.WedgeConfig>({
            defaultOptions: DefaultShapesOptions.SIZE_FILL,
            options: {
                angle: 45,
                radius: 100,
                ...additionalOptions
            },
        }))
    }

    /**
     * Crea un codice a barre con un codice predefinito
     * @param additionalOptions
     */
    public produceBarcode(additionalOptions?: Partial<Pamela.BarcodeConfig>): Pamela.Barcode {
        return new Pamela.Barcode(this.produceShapeConfig<Pamela.BarcodeConfig>({
            defaultOptions: DefaultShapesOptions.BARCODE,
            options: additionalOptions
        }))
    }

    /**
     * Crea una export variable
     * @param additionalOptions
     */
    public produceExportVariable(additionalOptions?: Partial<Pamela.ExportVariableConfig>): Pamela.ExportVariable {
        return new Pamela.ExportVariable(this.produceShapeConfig<Pamela.ExportVariableConfig>({
            defaultOptions: DefaultShapesOptions.EXPORT_VARIABLE,
            options: additionalOptions
        }))
    }

    /**
     * Produce un testo multistile
     * @param html contenuto iniziale html
     * @param additionalOptions opzioni aggiuntive
     */
    public produceRichText(html?: string, additionalOptions?: Partial<Pamela.RichTextConfig>): Pamela.RichText {
        let cont = DefaultShapesOptions.RICH_TEXT.htmlContent;

        const text = new Pamela.RichText(
            this.produceShapeConfig<Pamela.RichTextConfig>({
                defaultOptions: DefaultShapesOptions.RICH_TEXT,
                options: {htmlContent: cont, fill: 'black', ...additionalOptions}
            }));

        // Applico il contenuto se viene richiesto un override
        if (html)
            text.htmlContent(html);

        return text;
    }

    /**
     * Restituisce il colore da usare per la forma
     * @param options
     * @private
     */
    private getColor(options: ShapeCreationOptions): string {
        if (options.color) return options.color;
        if (options.defaultOptions?.fill) return options.defaultOptions?.fill;
        if (options.options?.fill) return options.options?.fill;
        return Pamela.Util.getRandomColor();
    }

    private getDefaultTableBuilder(): TableBuilder {
        const tBuilder = new TableBuilder({
            width: 300,
            x: 100,
            y: 100,
            height: 300,
            draggable: true,
            stroke: 'black',
            strokeWidth: 2
        });

        const borderPreset = {
            bordered: true,
            borderWidth: 2,
            borderColor: 'black'
        } as Pamela.BorderConfig;

        tBuilder
            .addColumn({
                column: ColumnBuilder
                    .withCells(9, {
                        bold: true,
                        bottomBorder: borderPreset,
                        rightBorder: borderPreset,
                        topBorder: borderPreset,
                        textAlign: HorizontalAlignment.Left,
                        textColor: 'black',
                        padding: 5,
                        fontSize: 20
                    })
                    .customWidth(70)
                    .setFirst({
                        bold: false
                    })
                    .populate([
                        'Valori nutrizionali',
                        'Energia (kj)',
                        'Energia (kcal)',
                        'Grassi',
                        'di cui grassi saturi',
                        'Carboidrati',
                        'di cui zuccheri',
                        'Fibre',
                        'Proteine',
                        'Sale'
                    ])
                    .set(3, {
                        textAlign: HorizontalAlignment.Right,
                        bold: false
                    })
                    .set(5, {
                        textAlign: HorizontalAlignment.Right,
                        bold: false
                    })
            })
            .addColumn({
                column: ColumnBuilder
                    .withCells(9, {
                        bottomBorder: borderPreset,
                        rightBorder: borderPreset,
                        topBorder: borderPreset,
                        textAlign: HorizontalAlignment.Right,
                        textColor: 'black',
                        padding: 8,
                        fontSize: 20
                    })
                    .setFirst({bold: true})
                    .autoWidth()
                    .populate([
                        '100g',
                        '1156 kj',
                        '383 kcal',
                        '0 g',
                        '0 g',
                        '88 g',
                        '53 g',
                        '1,3 g',
                        '7,1 g',
                        '0,08 g'
                    ])
            });


        return tBuilder;
    };
}